import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { ensureDownloadToken } from "@/lib/api";

// Full-screen image viewer. Tap the backdrop or the top-right button to close.
// Retries once with a refreshed download token if the signed URL has expired.
export function Lightbox({ src, alt = "photo", onClose }) {
  const [current, setCurrent] = useState(src);
  const [retried, setRetried] = useState(false);

  useEffect(() => {
    setCurrent(src);
    setRetried(false);
  }, [src]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleError = async () => {
    if (retried || !src || src.startsWith("data:")) return;
    setRetried(true);
    await ensureDownloadToken().catch(() => {});
    const sep = src.includes("?") ? "&" : "?";
    setCurrent(`${src}${sep}t=${Date.now()}`);
  };

  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/30"
        data-testid="lightbox-close"
      >
        <X className="h-6 w-6" />
      </button>
      <img
        src={current}
        alt={alt}
        onError={handleError}
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded-md object-contain"
        data-testid="lightbox-image"
      />
    </div>
  );
}
